
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  pregnancyStartDate: z.string().min(1, "La date est requise"),
  lastCheckupDate: z.string().optional(),
  nextCheckupDate: z.string().optional(),
  notes: z.string().optional(),
});

interface GestationFormProps {
  gestationData: any;
  onSuccess: () => void;
  initialData: {
    pregnancyStartDate?: Date;
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const GestationForm = ({ gestationData, onSuccess, initialData, open, onOpenChange }: GestationFormProps) => {
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      pregnancyStartDate: initialData.pregnancyStartDate?.toISOString().split('T')[0] || "",
      lastCheckupDate: gestationData?.last_checkup_date || "",
      nextCheckupDate: gestationData?.next_checkup_date || "",
      notes: gestationData?.notes || "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        toast({
          variant: "destructive",
          title: "Erreur d'authentification",
          description: "Veuillez vous connecter pour enregistrer vos données.",
        });
        return;
      }

      if (!user) {
        toast({
          variant: "destructive",
          title: "Non authentifié",
          description: "Vous devez être connecté pour enregistrer vos données.",
        });
        return;
      }

      console.log("Submitting data:", { ...values, user_id: user.id });

      const { error: upsertError } = await supabase
        .from("gestation_tracking")
        .upsert({
          user_id: user.id,
          pregnancy_start_date: values.pregnancyStartDate,
          last_checkup_date: values.lastCheckupDate || null,
          next_checkup_date: values.nextCheckupDate || null,
          notes: values.notes || null,
        });

      if (upsertError) {
        console.error("Error in upsert:", upsertError);
        throw upsertError;
      }

      toast({
        title: "Succès",
        description: "Vos informations ont été enregistrées",
      });
      
      onSuccess();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'enregistrer les informations",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Informations de grossesse</CardTitle>
        <CardDescription>
          Renseignez les informations de votre grossesse
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="pregnancyStartDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date de début de grossesse</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="lastCheckupDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dernier rendez-vous</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nextCheckupDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prochain rendez-vous</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full">
              {gestationData ? "Mettre à jour" : "Enregistrer"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
